/**
 * TutorialScreen — Chapter 2 (gameplay) of the guided onboarding: the REAL
 * production GameScene driven by the scripted multi-scenario transport,
 * presented through the unified spotlight system inside a shell that looks
 * IDENTICAL to the real gameplay screen:
 *
 *   - the real app Sidebar on the left (desktop full / tablet collapsed) and
 *     the real TopNav + Drawer on mobile — rendered directly, WITHOUT
 *     AppShell's social chrome (no FriendProvider subscription, no toasts,
 *     no active-game banner), so the isolation contract holds;
 *   - GameScreen's stage | info | ad grid on the right, with the real
 *     MatchCard and PlayersCard (fed by the same GameScene window events);
 *     chat is deliberately absent (it would open a real session channel).
 *
 * Round endings ride the PRODUCTION Round End / Match End overlays with
 * inert tutorial callbacks. No API call, no ActionCable, no session —
 * structurally unable to touch matchmaking, statistics, rankings or match
 * history.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Phaser from 'phaser';
import { GameScene } from '../scenes/GameScene';
import { ScriptedGameTransport, type ScriptedTransportEvent } from '../tutorial/ScriptedGameTransport';
import { createTutorialCleanup } from '../tutorial/tutorialLifecycle';
import {
  GAMEPLAY_TOTAL_STEPS,
  INITIAL_GAMEPLAY_STATE,
  TUTORIAL_STEPS,
  reduceGameplay,
  stepTitle,
  stepBody,
  stepSectionLabel,
} from '../tutorial/tutorialDirector';
import { useTranslation } from '../i18n/react';
import { t as translate } from '../i18n';
import { getPlayerName } from '../utils/playerName';
import { SpotlightOverlay } from '../components/tutorial/SpotlightOverlay';
import { CoachCard } from '../components/tutorial/CoachCard';
import { DragGuide } from '../components/tutorial/DragGuide';
import { TutorialLayer } from '../components/tutorial/TutorialLayer';
import { CUE_EVENT, type CueDetail } from '../tutorial/tutorialActionCues';
import { DS2Modal } from '../components/ui/DS2Modal';
import { OrdinaryRoundEndOverlay } from '../components/game/OrdinaryRoundEndOverlay';
import { MatchEndOverlay } from '../components/game/MatchEndOverlay';
import { MatchCard } from '../components/game/MatchCard';
import { PlayersCard, type PlayerRow } from '../components/game/PlayersCard';
import { GameChatSection } from '../components/game/GameChatSection';
import { PreferenceSettings } from '../config/preferences';
import { Sidebar } from '../components/shell/Sidebar';
import { TopNav } from '../components/shell/TopNav';
import { Drawer } from '../components/shell/Drawer';
import { AppShellProvider, useAppShell } from '../context/AppShellContext';
import { usePhaserTargets } from '../tutorial/spotlight/usePhaserTargets';
import { useDomTargetRects, scrollDomTargetIntoView } from '../tutorial/spotlight/domTargets';
import { resolveDisplayedGeometry, type SpotlightGeometry } from '../tutorial/spotlight/geometryRetention';
import type { SpotRect } from '../tutorial/spotlight/spotlightGeometry';
import { TUTORIAL_GAME_ID, TUTORIAL_SESSION_ID, TUTORIAL_TIMELINE } from '../tutorial/fixtures/tutorialFixtures';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import { stageSquareSide, stageAvailableHeight } from '../utils/stageSquare';
import { DS2, DT } from '../theme/designTokens';
import { useAuth } from '../context/AuthContext';
import { useIdentity, resolveDisplayName } from '../hooks/useIdentity';
import { GS_INFO_COL, GS_AD_COL } from './gameScreenGrid';
import type { GamePlayersUpdateDetail } from '../types/game';
import type { RoundEndPayload, RoundEndExitedPayload, NextRoundReadyPayload } from '../types/roundEnd';

/** The scripted timeline's authoritative match target. The fixture always
 *  carries it (pinned by tutorialTableTitle.test); there is deliberately NO
 *  numeric fallback — a future fixture that dropped the value must fail
 *  loudly here rather than silently rendering a wrong target. */
const TUTORIAL_TARGET_SCORE: number = (() => {
  const target = TUTORIAL_TIMELINE.boot.target_score;
  if (typeof target !== 'number') {
    throw new Error('Tutorial timeline boot state is missing target_score');
  }
  return target;
})();

/** Feedback state holds a stable KEY (never prose) so an already-visible
 *  reminder relabels on a runtime language switch. */
type FeedbackKey = 'wrongTile' | 'dragRequired';

/** Same stacked-tier breakpoint and stage backdrop as GameScreen. */
const TUT_STACK_CONTENT = 760;
const HERO_GRADIENT =
  'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.07) 0%, transparent 60%), ' +
  'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%), ' +
  '#080600';

/** GameScreen's responsive grid, tutorial-scoped (chat column omitted —
 *  tutorial has no session, so no chat). Same tiers, columns, surfaces. */
const TUT_GRID_CSS = `
.tut-container { container-type: inline-size; container-name: tutroot; width: 100%; height: 100%; }
.tut-grid {
  width: 100%; height: auto; min-height: 100%;
  display: grid; overflow: visible; background: ${DS2.color.surface};
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  grid-template-areas: "stage" "info";
}
.tut-stage { grid-area: stage; width: 100%; display: flex; align-items: flex-start; justify-content: center; overflow: hidden; background: ${HERO_GRADIENT}; }
.tut-info { grid-area: info; height: auto; overflow: visible; background: ${DS2.color.surface}; display: flex; flex-direction: column; padding: 16px max(10px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left)); gap: 16px; }
.tut-cards { order: 2; display: flex; flex-direction: row; gap: 10px; align-items: stretch; }
.tut-cards > * { flex: 1; min-width: 0; }
.tut-chat { order: 1; flex: none; display: flex; flex-direction: column; }
.tut-ad { display: none; }
.tut-ad span { font-size: 60px; color: #000; font-family: sans-serif; }
@container tutroot (max-width: 479.98px) {
  .tut-cards { flex-direction: column; gap: 12px; }
  .tut-cards > * { flex: none; min-width: 0; }
}
@container tutroot (min-width: ${TUT_STACK_CONTENT}px) {
  .tut-grid {
    height: 100%; min-height: 0; overflow: hidden;
    grid-template-columns: minmax(0, 1fr) ${GS_INFO_COL}px;
    grid-template-rows: 1fr;
    grid-template-areas: "stage info";
  }
  .tut-info { height: 100%; overflow-y: auto; overflow-x: hidden; padding: 16px 16px 14px; gap: 16px; }
  .tut-cards { order: 0; flex-direction: column; gap: 16px; flex-shrink: 0; }
  .tut-cards > * { flex: none; }
  .tut-chat { order: 0; flex: 1; min-height: 0; }
  .tut-ad { display: none; }
}
@container tutroot (min-width: 1100px) {
  .tut-grid {
    grid-template-columns: minmax(0, 1fr) ${GS_INFO_COL}px ${GS_AD_COL}px;
    grid-template-areas: "stage info ad";
  }
  .tut-ad { grid-area: ad; display: flex; align-items: center; justify-content: center; background: #BCF2FF; }
}
`;

export function TutorialScreen() {
  // Remount key: "Replay Gameplay Tutorial" tears everything down (Phaser,
  // transport, director) and boots the chapter fresh — in-memory only.
  const [runId, setRunId] = useState(0);
  return (
    <AppShellProvider>
      <TutorialRun key={runId} onReplay={() => setRunId((n) => n + 1)} />
    </AppShellProvider>
  );
}

function TutorialRun({ onReplay }: { onReplay: () => void }) {
  const navigate = useNavigate();
  const { user, isLoggedIn, devDisplayName } = useAuth();
  const identity = useIdentity();
  const { drawerOpen, closeDrawer } = useAppShell();
  const mountRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const transportRef = useRef<ScriptedGameTransport | null>(null);

  const { t } = useTranslation('tutorial');
  const [director, dispatch] = useReducer(reduceGameplay, INITIAL_GAMEPLAY_STATE);
  const [feedback, setFeedback] = useState<FeedbackKey | null>(null);
  const feedbackTimer = useRef<number | null>(null);
  const [hud, setHud] = useState<{ us: number; them: number }>({ us: 0, them: 0 });
  const [roundEndPayload, setRoundEndPayload] = useState<RoundEndPayload | null>(null);
  const [livePlayers, setLivePlayers] = useState<GamePlayersUpdateDetail[] | null>(null);
  // Chat parity: the REAL GameChatSection with an inert send — presentational
  // only (the networked useChatState hook is deliberately not mounted; a
  // tutorial has no session channel). Same initial-visibility preference read
  // as GameScreen (read-only).
  const [chatOpen, setChatOpen] = useState(false);
  const [chatVisible] = useState(() => PreferenceSettings.getTableChatEnabled());
  // Same stacked-tier chat fill decision as GameScreen (content width).
  const [isNarrow, setIsNarrow] = useState(false);

  // Same logged-in chrome resolution as AppShell (logged-out chrome renders
  // the real Sign Up / Log In sidebar, exactly like the public /rules page).
  const sidebarUser = isLoggedIn
    ? {
        displayName: resolveDisplayName(identity, user, devDisplayName || 'Player'),
        avatarKey:   identity?.avatarKey ?? null,
        avatarUrl:   identity?.avatarUrl ?? undefined,
      }
    : null;

  const exit = useCallback(() => {
    navigate(isLoggedIn ? '/home' : '/rules', { replace: true });
  }, [navigate, isLoggedIn]);
  const exitRef = useRef(exit);
  exitRef.current = exit;

  // ── Phaser bootstrap (once per run) ─────────────────────────────────
  useEffect(() => {
    if (!mountRef.current || gameRef.current) return;

    const transport = new ScriptedGameTransport({
      onEvent: (evt: ScriptedTransportEvent) => {
        if (evt.type === 'wrong-action' || evt.type === 'drag-required') {
          // Calm inline hints; the drag reminder never restarts or punishes.
          setFeedback(evt.type === 'drag-required' ? 'dragRequired' : 'wrongTile');
          if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current);
          feedbackTimer.current = window.setTimeout(() => setFeedback(null), 2500);
          return;
        }
        dispatch(evt);
      },
    });
    transportRef.current = transport;

    (window as Window & { __GAME_INIT__?: unknown }).__GAME_INIT__ = {
      gameId: TUTORIAL_GAME_ID,
      sessionId: TUTORIAL_SESSION_ID,
      useDesktopRender: IS_MOBILE_EXPERIENCE,
      viewerTileSkin: 'classic',
      viewerTableSkin: 'green_fabric',
    };

    /** Hands off to GameScene with the scripted transport injected. */
    class TutorialInitScene extends Phaser.Scene {
      constructor() { super({ key: 'InitScene' }); }
      create(): void {
        this.scene.start('GameScene', {
          gameId: TUTORIAL_GAME_ID,
          sessionId: TUTORIAL_SESSION_ID,
          teamScores: {},
          useDesktopRender: IS_MOBILE_EXPERIENCE,
          viewerTileSkin: 'classic',
          viewerTableSkin: 'green_fabric',
          transport,
        });
      }
    }

    /** Intercepts GameScene's legacy scene.start('MenuScene') escapes. */
    class TutorialExitScene extends Phaser.Scene {
      constructor() { super({ key: 'MenuScene' }); }
      create(): void { window.dispatchEvent(new CustomEvent('phaser-exit-to-menu')); }
    }

    // Same square rule + tested helpers as GameScreen: bounded by BOTH the
    // stage width and the visible height (stacked tier measures viewport).
    const applySquare = () => {
      const grid = gridRef.current;
      const stage = stageRef.current;
      const mount = mountRef.current;
      if (!grid || !stage || !mount) return;
      const stacked = grid.getBoundingClientRect().width < TUT_STACK_CONTENT;
      const rect = stage.getBoundingClientRect();
      const vh = window.visualViewport?.height ?? document.documentElement.clientHeight;
      const availH = stageAvailableHeight(stacked, rect.top, rect.height, vh);
      const side = stageSquareSide(rect.width, availH);
      if (side <= 0) return;
      const px = `${side}px`;
      if (mount.style.width !== px) mount.style.width = px;
      if (mount.style.height !== px) mount.style.height = px;
    };
    applySquare();

    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: mountRef.current,
      backgroundColor: '#000000',
      scene: [TutorialInitScene, GameScene, TutorialExitScene],
      scale: { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.NO_CENTER },
    });
    gameRef.current = game;

    const resync = () => {
      applySquare();
      gameRef.current?.scale.refresh();
    };
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(resync);
      resizeObserver.observe(mountRef.current);
      if (stageRef.current) resizeObserver.observe(stageRef.current);
      if (gridRef.current) resizeObserver.observe(gridRef.current);
    }
    window.visualViewport?.addEventListener('resize', resync);

    const handleExit = () => exitRef.current();
    window.addEventListener('phaser-exit-to-menu', handleExit);
    window.addEventListener('leave-match-request', handleExit);

    const cleanup = createTutorialCleanup({
      game,
      disposeTransport: () => transport.dispose(),
      removeListeners: () => {
        resizeObserver?.disconnect();
        window.visualViewport?.removeEventListener('resize', resync);
        window.removeEventListener('phaser-exit-to-menu', handleExit);
        window.removeEventListener('leave-match-request', handleExit);
        if (feedbackTimer.current !== null) {
          window.clearTimeout(feedbackTimer.current);
          feedbackTimer.current = null;
        }
      },
    });
    return () => {
      cleanup();
      gameRef.current = null;
      transportRef.current = null;
    };
  }, []);

  // ── React ↔ Phaser bridge (HUD, players panel, round-end lifecycle) ─
  useEffect(() => {
    const onHud = (e: Event) => {
      const d = (e as CustomEvent).detail;
      setHud({ us: d.usScore ?? 0, them: d.themScore ?? 0 });
    };
    const onPlayers = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (Array.isArray(detail)) setLivePlayers(detail as GamePlayersUpdateDetail[]);
    };
    const onRoundEnd = (e: Event) => {
      setRoundEndPayload((e as CustomEvent<RoundEndPayload>).detail);
      dispatch({ type: 'round-end-shown' });
    };
    const onNextRoundReady = (e: Event) => {
      const detail = (e as CustomEvent<NextRoundReadyPayload>).detail;
      setRoundEndPayload(null);
      window.dispatchEvent(new CustomEvent<RoundEndExitedPayload>('react:round-end-exited', {
        detail: { gameId: detail?.gameId ?? 0 },
      }));
    };
    const measureNarrow = () => {
      const g = gridRef.current;
      if (g) setIsNarrow(g.getBoundingClientRect().width < TUT_STACK_CONTENT);
    };
    measureNarrow();
    window.addEventListener('resize', measureNarrow);
    window.addEventListener('game-hud-update', onHud);
    window.addEventListener('phaser:players-update', onPlayers);
    window.addEventListener('phaser:round-end', onRoundEnd);
    window.addEventListener('phaser:next-round-ready', onNextRoundReady);
    return () => {
      window.removeEventListener('resize', measureNarrow);
      window.removeEventListener('game-hud-update', onHud);
      window.removeEventListener('phaser:players-update', onPlayers);
      window.removeEventListener('phaser:round-end', onRoundEnd);
      window.removeEventListener('phaser:next-round-ready', onNextRoundReady);
    };
  }, []);

  // ── Step engine ─────────────────────────────────────────────────────
  const step = TUTORIAL_STEPS[director.index] ?? null;
  const complete = director.index >= TUTORIAL_STEPS.length;

  const enteredRef = useRef(-1);
  useEffect(() => {
    if (!step || enteredRef.current === director.index) return;
    enteredRef.current = director.index;
    if (step.enter === 'advance-scenario') transportRef.current?.advanceScenario();
    if (step.enter === 'run-auto') transportRef.current?.runAuto();
    for (const id of step.targets.dom ?? []) scrollDomTargetIntoView(id);
  }, [director.index, step]);

  const phaserIds = step ? [...(step.targets.phaser ?? []), ...(step.targets.phaserPassthrough ?? [])] : [];
  const phaserBounds = usePhaserTargets(phaserIds);
  const domBounds = useDomTargetRects(step?.targets.dom ?? []);

  const cutouts: SpotRect[] = [];
  const passthroughs: SpotRect[] = [];
  if (step) {
    for (const id of step.targets.phaser ?? []) cutouts.push(...(phaserBounds[id] ?? []));
    for (const id of step.targets.dom ?? []) cutouts.push(...(domBounds[id] ?? []));
    for (const id of step.targets.phaserPassthrough ?? []) passthroughs.push(...(phaserBounds[id] ?? []));
  }
  const fullDim = step?.targets.fullDim === true;
  const showContinue = step?.advance.on === 'continue';

  // Last-valid-geometry retention (bottom blur-gap fix): while the new
  // step's targets are still measuring, keep the previous complete geometry
  // on screen — rendered inert — and swap atomically once ready. Intentional
  // no-target steps keep their approved no-overlay presentation and clear
  // the snapshot. See spotlight/geometryRetention.ts.
  const lastGeomRef = useRef<SpotlightGeometry | null>(null);
  const stepHasTargets = !!step && (
    (step.targets.phaser?.length ?? 0) > 0 ||
    (step.targets.dom?.length ?? 0) > 0 ||
    fullDim
  );
  const resolved = resolveDisplayedGeometry({
    last: lastGeomRef.current,
    stepHasTargets: stepHasTargets && !complete,
    fullDim,
    cutouts,
    passthroughs,
  });
  lastGeomRef.current = resolved.nextLast;
  const geom = resolved.geometry;
  const retained = resolved.retained;
  const interactive = step?.targets.interactive === true;

  // The production drag/selection previews existing = the tile is picked up.
  const previewsActive = (phaserBounds['placement-previews'] ?? []).length > 0;

  // ── Action cue (Phaser scale pulse on the required tile) ────────────
  // Only interactive steps cue; the pulse stops the moment the tile is
  // picked up (previews visible) and whenever the step changes/completes.
  const cueTargetId = step?.targets.phaser?.find((t) => t.startsWith('hand-tile:'));
  const cueTileId = interactive && !previewsActive && cueTargetId
    ? Number(cueTargetId.slice('hand-tile:'.length))
    : null;
  useEffect(() => {
    window.dispatchEvent(new CustomEvent<CueDetail>(CUE_EVENT, { detail: { tileId: cueTileId } }));
  }, [cueTileId]);
  useEffect(() => () => {
    // Unmount/replay/exit: always clear any live cue.
    window.dispatchEvent(new CustomEvent<CueDetail>(CUE_EVENT, { detail: { tileId: null } }));
  }, []);

  // ── Drag lesson extras ──────────────────────────────────────────────
  const isDragLesson = step?.dragLesson === true;
  // Guide from the required tile to the glowing end — measured bounds only;
  // removed the moment the real drag begins (previews appear).
  const guideFrom = isDragLesson && !previewsActive && cueTargetId
    ? (phaserBounds[cueTargetId] ?? [])[0]
    : undefined;
  const guideToId = isDragLesson ? step?.targets.phaser?.find((t) => t.startsWith('board-tile:')) : undefined;
  const guideTo = guideToId ? (phaserBounds[guideToId] ?? [])[0] : undefined;

  const onContinue = useCallback(() => dispatch({ type: 'continue' }), []);
  const onSkip = useCallback(() => dispatch({ type: 'skip-chapter' }), []);

  // Inert production-overlay callbacks — never any backend contact.
  const inertReady = useCallback(async (_sessionId: number) => {}, []);
  const inertVote = useCallback(async (_sessionId: number, _vote: 'yes' | 'no') => {}, []);
  const noop = useCallback(() => {}, []);

  // Players panel rows — identical mapping to GameScreen's: the shared
  // getPlayerName seam localizes the scripted seats (self → You/Tú, bots by
  // stable difficulty) exactly like production, and the pre-hydration
  // fallback rows use the same gameplay:fallback.* keys as GameScreen.
  const toRow = (p: GamePlayersUpdateDetail): PlayerRow => ({
    label:       getPlayerName(p, String(translate('gameplay:fallback.player'))),
    isMe:        p.is_me,
    isBot:       p.is_bot,
    countryCode: p.country_code ?? null,
    avatarKey:   p.avatar_key ?? null,
    resolved:    true,
    canAdd:      false,
  });
  const teamA: PlayerRow[] = livePlayers
    ? livePlayers.filter(p => p.team === 0).sort((a, b) => a.position - b.position).map(toRow)
    : [{ label: String(translate('gameplay:fallback.you')), isMe: true },
       { label: String(translate('gameplay:fallback.partner')), isMe: false }];
  const teamB: PlayerRow[] = livePlayers
    ? livePlayers.filter(p => p.team === 1).sort((a, b) => a.position - b.position).map(toRow)
    : [{ label: String(translate('gameplay:fallback.opponent')), isMe: false },
       { label: String(translate('gameplay:fallback.opponent')), isMe: false }];

  return (
    <div
      style={{
        position:   'fixed',
        inset:      0,
        width:      '100vw',
        height:     '100dvh',
        background: DT.color.pageBg,
        overflow:   'hidden',
        display:    'flex',
      }}
    >
      {/* Real app chrome, exactly as the gameplay screen shows it — but with
          NO social providers/toasts/banners (isolation contract). */}
      <div className="hidden md:flex" style={{ height: '100%' }}>
        <div className="flex lg:hidden" style={{ height: '100%' }}>
          <Sidebar variant="collapsed" user={sidebarUser} />
        </div>
        <div className="hidden lg:flex" style={{ height: '100%' }}>
          <Sidebar variant="full" user={sidebarUser} />
        </div>
      </div>
      <div className="md:hidden">
        <TopNav ds2 />
      </div>
      <Drawer open={drawerOpen} onClose={closeDrawer} ds2 />

      <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden" style={{ display: 'flex', flexDirection: 'column', overscrollBehavior: 'contain', paddingBottom: DT.layout.safeAreaBottom }}>
        {/* Matches TopNav's own height, including its status-bar/notch inset —
            see the identical spacer in components/shell/AppShell.tsx. */}
        <div className="md:hidden" style={{ height: DT.layout.topNavHeightCss, flexShrink: 0 }} aria-hidden />
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <style>{TUT_GRID_CSS}</style>
          <div className="tut-container">
            <div className="tut-grid" ref={gridRef}>
              <div className="tut-stage" ref={stageRef}>
                <div ref={mountRef} />
              </div>
              <div className="tut-info">
                <div className="tut-cards">
                  {/* Real score display — the 'score-display' spotlight target. */}
                  <div data-tutorial-target="score-display">
                    <MatchCard
                      playerName={sidebarUser?.displayName ?? ''}
                      countryCode={identity?.countryCode}
                      // Semantic self-table title (Your table · N points / Tu
                      // mesa · N puntos) — the scripted table has no account
                      // identity behind it. Target comes from the fixture's
                      // authoritative target_score, never hardcoded here.
                      viewerTable
                      target={TUTORIAL_TARGET_SCORE}
                      usScore={hud.us}
                      themScore={hud.them}
                    />
                  </div>
                  <PlayersCard teamA={teamA} teamB={teamB} onAdd={noop} />
                </div>
                {chatVisible && (
                  <div className="tut-chat">
                    <GameChatSection
                      messages={[]}
                      sendMessage={noop}
                      open={chatOpen}
                      onToggle={() => setChatOpen(o => !o)}
                      fill={!isNarrow}
                    />
                  </div>
                )}
              </div>
              <div className="tut-ad"><span>ad</span></div>
            </div>
          </div>
        </div>
      </main>

      {/* Production end-of-round presentation with inert tutorial callbacks. */}
      {roundEndPayload && (roundEndPayload.sessionOver ? (
        <MatchEndOverlay payload={roundEndPayload} onRematchVote={inertVote} />
      ) : (
        <OrdinaryRoundEndOverlay
          payload={roundEndPayload}
          onReady={inertReady}
          readyStatus="idle"
          exiting={false}
          onOrdinaryExited={noop}
          viewerTileSkin="classic"
        />
      ))}

      {/* Unified spotlight presentation via the inert-exempt body portal. */}
      <TutorialLayer>
        {/* Unified spotlight presentation. Overlays render once the real
            target is measurable (holding the previous step's geometry,
            inert, while the next one measures); watch phases show the card
            alone. A retained frame is never interactive — no buffered click
            can reach the next step's target through a stale opening. */}
        {!complete && geom && (
          <SpotlightOverlay
            cutouts={geom.cutouts}
            passthroughs={geom.passthroughs}
            interactive={interactive && !retained}
            fullDim={geom.fullDim}
            animateCues={interactive && !retained}
            suppressBlocking={isDragLesson && previewsActive && !retained}
          />
        )}
        {!complete && guideFrom && guideTo && (
          <DragGuide from={guideFrom} to={guideTo} />
        )}
        {step && (
          <CoachCard
            progress={t('progress.chapter2', { section: stepSectionLabel(step), current: director.index + 1, total: GAMEPLAY_TOTAL_STEPS })}
            title={stepTitle(step)}
            body={stepBody(step)}
            hint={feedback ? t(`feedback.${feedback}`) : null}
            avoid={[...cutouts, ...passthroughs]}
            isMobileLayout={IS_MOBILE_EXPERIENCE}
            onContinue={showContinue ? onContinue : undefined}
            onSkipChapter={onSkip}
            onExit={exit}
          />
        )}
      </TutorialLayer>

      {/* True completion modal. */}
      {complete && (
        <DS2Modal
          open
          title={t('complete.title')}
          description={director.skipped ? t('complete.bodySkipped') : t('complete.body')}
          actions={[
            { id: 'exit', label: t('complete.exit'), variant: 'gold', autoFocus: true },
            { id: 'replay-tour', label: t('complete.replayTour'), variant: 'neutral' },
            { id: 'replay-gameplay', label: t('complete.replayGameplay'), variant: 'neutral' },
          ]}
          onAction={(id) => {
            if (id === 'exit') exit();
            else if (id === 'replay-tour') navigate('/home?tour=app');
            else onReplay();
          }}
          onDismiss={exit}
          compact
        />
      )}
    </div>
  );
}
